import React, { useEffect, useRef, useState } from "react";
import * as LucideIcons from "lucide-react";
import FluxoCanvas from "./automacao/FluxoCanvas";
import FluxoVertical from "./automacao/FluxoVertical";
import { FLUXOS, SEQUENCIA } from "../data/fluxosAutomacao";

/**
 * SecaoAutomacaoVisual — "Automação na prática", logo abaixo da ProvaSocial.
 *
 * Mostra UMA OPERAÇÃO ACONTECENDO: o cliente manda a mensagem, a IA responde,
 * o sistema consulta o estoque, a decisão ramifica, a venda fecha e a equipe
 * entrega. Cada card é um pedaço da interface real, e um card fica ativo por
 * vez, em sequência — a leitura é de execução, não de diagrama.
 *
 * Mostra a automação rodando end-to-end pra cada nicho — do primeiro contato à venda.
 *
 * React + CSS + SVG, sem biblioteca. Animações escopadas (prefixo av-),
 * pausadas fora da viewport e desligadas em prefers-reduced-motion.
 */

const PASSO_MS = 1700;

const Icone = ({ nome, ...props }) => {
  const C = LucideIcons[nome] || LucideIcons.Circle;
  return <C {...props} />;
};

const CSS_SECAO = `
.av-raiz .av-surgir { opacity: 0; transform: translateY(14px); }
.av-raiz[data-entrou="true"] .av-surgir {
  animation: av-surgir .8s cubic-bezier(.22,1,.36,1) var(--av-atraso, 0ms) both;
}
@keyframes av-surgir { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }

/* Card: transições de estado (ativo / atenuado) */
.av-no { transition: border-color .5s ease, box-shadow .5s ease, opacity .5s ease, transform .5s ease; }
.av-raiz[data-entrou="true"] .av-no-ativo { transform: translateY(-2px); }
.av-no-atenuado { opacity: .5; }

/* Ponto de luz percorrendo a conexão ativa */
.av-luz { stroke-dasharray: 0.6 99.4; opacity: 0; }
.av-raiz[data-entrou="true"] .av-luz-ativa { animation: av-luz 1.5s cubic-bezier(.4,0,.5,1) forwards; }
@keyframes av-luz {
  0% { stroke-dashoffset: 0; opacity: 0; }
  12% { opacity: 1; }
  88% { opacity: 1; }
  100% { stroke-dashoffset: -100; opacity: 0; }
}

/* Halo da conexão ativa */
.av-halo-linha { opacity: 0; filter: blur(5px); }
.av-raiz[data-entrou="true"] .av-halo-linha { animation: av-halo-linha 1.5s ease-out forwards; }
@keyframes av-halo-linha { 0% { opacity: 0; } 30% { opacity: .7; } 100% { opacity: 0; } }

/* Indicador de processamento no card ativo — respiração, não pulso forte */
.av-raiz[data-entrou="true"] .av-pulso { animation: av-pulso 1.6s ease-in-out infinite; }
@keyframes av-pulso { 0%, 100% { opacity: .4; transform: scale(1); } 50% { opacity: 1; transform: scale(1.3); } }

.av-raiz[data-entrou="true"] .av-pisca { animation: av-pulso 1.8s ease-in-out infinite; }

/* "Digitando" da IA */
.av-digitando span { width: 3px; height: 3px; border-radius: 9999px; background: #86efac; opacity: .3; display: inline-block; }
.av-raiz[data-entrou="true"] .av-digitando span { animation: av-digitando 1.2s ease-in-out infinite; }
.av-digitando span:nth-child(2) { animation-delay: .2s !important; }
.av-digitando span:nth-child(3) { animation-delay: .4s !important; }
@keyframes av-digitando { 0%, 100% { opacity: .3; } 50% { opacity: 1; } }

/* Halos do palco — respiração muito lenta */
.av-raiz[data-entrou="true"] .av-halo { animation: av-halo 12s ease-in-out infinite; }
@keyframes av-halo { 0%, 100% { opacity: .75; } 50% { opacity: 1; } }

.av-raiz[data-ativo="false"] .av-luz-ativa,
.av-raiz[data-ativo="false"] .av-halo-linha,
.av-raiz[data-ativo="false"] .av-pulso,
.av-raiz[data-ativo="false"] .av-pisca,
.av-raiz[data-ativo="false"] .av-digitando span,
.av-raiz[data-ativo="false"] .av-halo { animation-play-state: paused; }

@media (prefers-reduced-motion: reduce) {
  .av-raiz .av-surgir,
  .av-raiz[data-entrou] .av-surgir {
    opacity: 1; transform: none; animation: none !important;
  }
  .av-no { transition: none; }
  .av-raiz[data-entrou="true"] .av-no-ativo { transform: none; }
  .av-no-atenuado { opacity: 1; }
  .av-luz, .av-halo-linha { display: none; }
  .av-pulso, .av-pisca, .av-digitando span, .av-halo { animation: none !important; opacity: 1; }
}

/* Revelação sequencial dos blocos e conectores */
@keyframes av-revela-bloco {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
}
@keyframes av-revela-losango {
    from { opacity: 0; transform: rotate(45deg) translateY(8px); }
    to   { opacity: 1; transform: rotate(45deg) translateY(0); }
}
@keyframes av-revela-conector {
    from { opacity: 0; transform: scaleX(0.85); transform-origin: left center; }
    to   { opacity: 1; transform: scaleX(1);    transform-origin: left center; }
}

.av-raiz .av-bloco { opacity: 0; }
.av-raiz .av-conector { opacity: 0; }

.av-raiz[data-revelado="1"] .av-bloco[data-ordem="1"],
.av-raiz[data-revelado="2"] .av-bloco[data-ordem="1"],
.av-raiz[data-revelado="2"] .av-bloco[data-ordem="2"],
.av-raiz[data-revelado="3"] .av-bloco[data-ordem="1"],
.av-raiz[data-revelado="3"] .av-bloco[data-ordem="2"],
.av-raiz[data-revelado="3"] .av-bloco[data-ordem="3"],
.av-raiz[data-revelado="4"] .av-bloco[data-ordem="1"],
.av-raiz[data-revelado="4"] .av-bloco[data-ordem="2"],
.av-raiz[data-revelado="4"] .av-bloco[data-ordem="3"],
.av-raiz[data-revelado="4"] .av-bloco[data-ordem="4"],
.av-raiz[data-revelado="5"] .av-bloco[data-ordem="1"],
.av-raiz[data-revelado="5"] .av-bloco[data-ordem="2"],
.av-raiz[data-revelado="5"] .av-bloco[data-ordem="3"],
.av-raiz[data-revelado="5"] .av-bloco[data-ordem="4"],
.av-raiz[data-revelado="5"] .av-bloco[data-ordem="5"],
.av-raiz[data-revelado="6"] .av-bloco[data-ordem="1"],
.av-raiz[data-revelado="6"] .av-bloco[data-ordem="2"],
.av-raiz[data-revelado="6"] .av-bloco[data-ordem="3"],
.av-raiz[data-revelado="6"] .av-bloco[data-ordem="4"],
.av-raiz[data-revelado="6"] .av-bloco[data-ordem="5"],
.av-raiz[data-revelado="6"] .av-bloco[data-ordem="6"],
.av-raiz[data-revelado="7"] .av-bloco[data-ordem="1"],
.av-raiz[data-revelado="7"] .av-bloco[data-ordem="2"],
.av-raiz[data-revelado="7"] .av-bloco[data-ordem="3"],
.av-raiz[data-revelado="7"] .av-bloco[data-ordem="4"],
.av-raiz[data-revelado="7"] .av-bloco[data-ordem="5"],
.av-raiz[data-revelado="7"] .av-bloco[data-ordem="6"],
.av-raiz[data-revelado="7"] .av-bloco[data-ordem="7"],
.av-raiz[data-revelado="8"] .av-bloco {
    animation: av-revela-bloco 400ms ease-out both;
}

.av-raiz[data-revelado="2"] .av-conector[data-ordem="2"],
.av-raiz[data-revelado="3"] .av-conector[data-ordem="2"],
.av-raiz[data-revelado="3"] .av-conector[data-ordem="3"],
.av-raiz[data-revelado="4"] .av-conector[data-ordem="2"],
.av-raiz[data-revelado="4"] .av-conector[data-ordem="3"],
.av-raiz[data-revelado="4"] .av-conector[data-ordem="4"],
.av-raiz[data-revelado="5"] .av-conector[data-ordem="2"],
.av-raiz[data-revelado="5"] .av-conector[data-ordem="3"],
.av-raiz[data-revelado="5"] .av-conector[data-ordem="4"],
.av-raiz[data-revelado="5"] .av-conector[data-ordem="5"],
.av-raiz[data-revelado="6"] .av-conector[data-ordem="2"],
.av-raiz[data-revelado="6"] .av-conector[data-ordem="3"],
.av-raiz[data-revelado="6"] .av-conector[data-ordem="4"],
.av-raiz[data-revelado="6"] .av-conector[data-ordem="5"],
.av-raiz[data-revelado="6"] .av-conector[data-ordem="6"],
.av-raiz[data-revelado="7"] .av-conector[data-ordem="2"],
.av-raiz[data-revelado="7"] .av-conector[data-ordem="3"],
.av-raiz[data-revelado="7"] .av-conector[data-ordem="4"],
.av-raiz[data-revelado="7"] .av-conector[data-ordem="5"],
.av-raiz[data-revelado="7"] .av-conector[data-ordem="6"],
.av-raiz[data-revelado="7"] .av-conector[data-ordem="7"],
.av-raiz[data-revelado="8"] .av-conector {
    animation: av-revela-conector 350ms ease-out 100ms both;
}

/* Reduced motion: tudo imediato, sem animação */
@media (prefers-reduced-motion: reduce) {
    .av-raiz .av-bloco,
    .av-raiz .av-conector {
        opacity: 1 !important;
        animation: none !important;
    }
}

.av-raiz .av-bloco[data-ordem="4"] {
    animation-name: av-revela-losango !important;
}

@media (prefers-reduced-motion: reduce) {
    .av-raiz .av-bloco[data-ordem="4"] {
        transform: rotate(45deg) !important;
    }
}
`;

function useVisibilidade(ref) {
  const [entrou, setEntrou] = useState(false);
  const [ativo, setAtivo] = useState(true);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setEntrou(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      ([entrada]) => {
        setAtivo(entrada.isIntersecting);
        if (entrada.isIntersecting) setEntrou(true);
      },
      { threshold: 0.1 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref]);

  return { entrou, ativo };
}

export default function SecaoAutomacaoVisual() {
  const ref = useRef(null);
  const { entrou, ativo } = useVisibilidade(ref);
  const [indice, setIndice] = useState(0);
  const [passo, setPasso] = useState(0);
  const [revelado, setRevelado] = useState(0);
  const fluxo = FLUXOS[indice];

  // Execução sequencial: avança uma etapa por vez enquanto a seção está
  // visível. Fora da tela ou com movimento reduzido, congela num passo em que
  // o fluxo continua legível.
  useEffect(() => {
    const reduzido =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduzido || !entrou || !ativo) return undefined;

    const t = setTimeout(() => setPasso((p) => (p + 1) % SEQUENCIA.length), PASSO_MS);
    return () => clearTimeout(t);
  }, [passo, entrou, ativo]);

  // Revelacao sequencial dos blocos do canvas (xl+). Cada bloco entra 500ms
  // depois do anterior e permanece visivel; o estado final nao reinicia.
  useEffect(() => {
    if (!entrou) {
      setRevelado(0);
      return undefined;
    }

    const reducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reducedMotion) {
      setRevelado(8);
      return undefined;
    }

    setRevelado(0);

    const timeouts = [];
    const DELAY_INICIAL = 400;
    const INTERVALO = 500;

    for (let i = 1; i <= 8; i += 1) {
      const atraso = DELAY_INICIAL + (i - 1) * INTERVALO;
      timeouts.push(setTimeout(() => setRevelado(i), atraso));
    }

    return () => timeouts.forEach(clearTimeout);
  }, [entrou, indice]);

  const trocarFluxo = (i) => {
    setIndice(i);
    setPasso(0);
  };

  return (
    <section
      ref={ref}
      id="automacao-visual"
      data-testid="secao-automacao-visual"
      aria-labelledby="automacao-titulo"
      className="av-raiz hidden md:block relative overflow-hidden py-20 md:py-24 px-4 md:px-6"
      style={{ background: "#080A09" }}
      data-entrou={entrou ? "true" : "false"}
      data-ativo={ativo ? "true" : "false"}
      data-revelado={revelado}
    >
      <style>{CSS_SECAO}</style>

      {/* Fundo: grid tecnológico sutil + verde quase preto */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.02) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
          WebkitMaskImage: "radial-gradient(ellipse 72% 62% at 50% 46%, #000 12%, transparent 74%)",
          maskImage: "radial-gradient(ellipse 72% 62% at 50% 46%, #000 12%, transparent 74%)",
        }}
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute left-1/2 top-0 h-[520px] w-[1100px] max-w-full -translate-x-1/2 rounded-full"
        style={{ background: "radial-gradient(ellipse, rgba(34,197,94,0.07) 0%, transparent 70%)", filter: "blur(60px)" }}
        aria-hidden="true"
      />

      {/* Header — enxuto, titulo em 2 linhas fixas alinhado ao mockup */}
      <div className="av-surgir relative mx-auto mb-8 flex max-w-7xl flex-col items-center gap-3 text-center md:mb-10">
        <div className="inline-flex items-center gap-2 rounded-full border border-green-500/30 bg-green-500/15 px-3 py-1">
          <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
          <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-green-400">
            Automação na prática
          </span>
        </div>
        <h2
          id="automacao-titulo"
          className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-extrabold leading-[1.1] text-white tracking-tight"
        >
          Seu negócio funcionando no <span className="text-green-400">automático</span>,<br />do primeiro contato à venda.
        </h2>
      </div>

      {/* Seletor de segmento */}
      <div
        className="av-surgir relative mx-auto mb-6 flex flex-wrap items-center justify-center gap-2 md:mb-8"
        style={{ "--av-atraso": "120ms" }}
        role="tablist"
        aria-label="Tipo de operação"
        data-testid="seletor-fluxo"
      >
        {FLUXOS.map((f, i) => {
          const selecionado = i === indice;
          return (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={selecionado}
              onClick={() => trocarFluxo(i)}
              data-testid={`fluxo-${f.id}`}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 ${
                selecionado
                  ? "border-green-500/40 bg-green-500/10 text-white"
                  : "border-white/[0.08] bg-[#121212] text-zinc-400 hover:border-white/20 hover:text-zinc-200"
              }`}
            >
              <Icone
                nome={f.icone}
                className={`h-3.5 w-3.5 ${selecionado ? "text-green-400" : "text-zinc-500"}`}
              />
              {f.nome}
            </button>
          );
        })}
      </div>

      {/* Palco da operação */}
      <div
        className="av-surgir relative mx-auto max-w-[1530px]"
        style={{ "--av-atraso": "200ms" }}
        role="img"
        aria-label={`Operação de ${fluxo.nome}: ${fluxo.etapas.map((e) => e.titulo).join(", depois ")}.`}
      >
        <div
          className="relative overflow-hidden rounded-3xl"
          style={{
            background: "linear-gradient(180deg, #0C0E0D 0%, #090B0A 100%)",
            border: "1px solid rgba(34,197,94,0.22)",
            boxShadow: "0 50px 120px -50px rgba(0,0,0,0.95), 0 0 60px -20px rgba(34,197,94,0.18), inset 0 0 0 1px rgba(34,197,94,0.06)",
          }}
        >
          {/* Padrao de pontos internos — dot grid discreto */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              backgroundImage: "radial-gradient(rgba(34,197,94,0.12) 1px, transparent 1px)",
              backgroundSize: "22px 22px",
              WebkitMaskImage: "radial-gradient(ellipse 85% 85% at 50% 50%, #000 40%, transparent 90%)",
              maskImage: "radial-gradient(ellipse 85% 85% at 50% 50%, #000 40%, transparent 90%)",
            }}
            aria-hidden="true"
          />

          {/* Linha luminosa verde no topo */}
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-px"
            style={{ background: "linear-gradient(90deg, transparent, rgba(34,197,94,0.5), transparent)" }}
            aria-hidden="true"
          />

          {/* Palco horizontal — xl+ */}
          {/* Trilha horizontal só a partir de xl: abaixo disso o palco de 1500
              escala para menos de 0.7 e o texto dos cards fica ilegível.
              Padding maior (24px) da respiro entre moldura e cards. */}
          <div key={`h-${fluxo.id}`} className="relative hidden p-4 xl:block xl:p-6">
            <FluxoCanvas fluxo={fluxo} passo={passo} />
          </div>

          {/* Coluna — abaixo de xl. Não é o palco comprimido: em tablet a
              trilha vira duas colunas de cards, mantendo a leitura. */}
          <div key={`v-${fluxo.id}`} className="relative p-4 xl:hidden xl:p-6">
            <FluxoVertical fluxo={fluxo} passo={passo} />
          </div>
        </div>
      </div>

      {/* Mesma operação em texto, para leitores de tela */}
      <ol className="sr-only">
        {fluxo.etapas.map((e) => (
          <li key={e.id}>
            {e.titulo}
            {e.descricao ? ` — ${e.descricao}` : ""}
          </li>
        ))}
      </ol>
    </section>
  );
}
